import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  NotificationApiItem,
  NotificationCategory,
  NotificationStats,
  NotificationsListResponse,
} from '../services/notificationsService';

/** Как у чата проекта/дела: опрос API без WebSocket */
export const NOTIFICATIONS_POLL_MS = 2000;

export const notificationKeys = {
  all: ['notifications'] as const,
  stats: () => [...notificationKeys.all, 'stats'] as const,
  list: (params: { filter: string; page: number; unreadOnly: boolean; category: string }) =>
    [...notificationKeys.all, 'list', params] as const,
};

export function useNotificationStatsQuery(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  return useQuery({
    queryKey: notificationKeys.stats(),
    queryFn: () => fetchNotifications({ page: 1, perPage: 1 }),
    select: (r: NotificationsListResponse): NotificationStats => r.stats,
    enabled,
    staleTime: 0,
    refetchInterval: enabled ? NOTIFICATIONS_POLL_MS : false,
  });
}

export function useNotificationListQuery(args: {
  filter: string;
  page: number;
  enabled: boolean;
}) {
  const { filter, page, enabled } = args;
  const unreadOnly = filter === 'Непрочитанное';
  let category: NotificationCategory | undefined;
  if (filter === 'Задачи') category = 'task';
  else if (filter === 'Проекты') category = 'project';
  else if (filter === 'Дела') category = 'case';
  else if (filter === 'Чаты') category = 'chat';
  else if (filter === 'События') category = 'event';
  else if (filter === 'Дни рождения') category = 'birthday';

  return useQuery({
    queryKey: notificationKeys.list({
      filter,
      page,
      unreadOnly,
      category: category ?? '',
    }),
    queryFn: () =>
      fetchNotifications({
        page,
        perPage: 25,
        category: unreadOnly ? undefined : category,
        unreadOnly,
      }),
    enabled,
    staleTime: 0,
    /** При уходе со страницы не держим список в кэше — при следующем заходе не показываем устаревшие данные */
    gcTime: 0,
    refetchOnMount: 'always',
    refetchInterval: enabled ? NOTIFICATIONS_POLL_MS : false,
    /** Чтобы при смене фильтра/страницы показывать оверлей, а не пустой экран; фоновый polling не ставит placeholder */
    placeholderData: (previousData) => previousData,
  });
}

type ListQueryParams = { filter: string; page: number; unreadOnly: boolean; category: string };

export function useMarkNotificationReadMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => markNotificationRead(id),
    onMutate: async (id: number) => {
      await qc.cancelQueries({ queryKey: notificationKeys.all });
      const listQueries = qc.getQueriesData<NotificationsListResponse>({
        predicate: (q) =>
          Array.isArray(q.queryKey) && q.queryKey[0] === 'notifications' && q.queryKey[1] === 'list',
      });
      const previous: Array<[readonly unknown[], NotificationsListResponse]> = [];

      for (const [key, data] of listQueries) {
        if (!data) continue;
        previous.push([key, data]);
        const params = (key as readonly [string, string, ListQueryParams])[2];
        const unreadOnly = Boolean(params?.unreadOnly);
        const item = data.data.find((x) => x.id === id);
        if (!item || item.is_read) continue;

        const readAt = new Date().toISOString();
        const nextRows: NotificationApiItem[] = unreadOnly
          ? data.data.filter((x) => x.id !== id)
          : data.data.map((x) => (x.id === id ? { ...x, is_read: true, read_at: readAt } : x));

        const nextStats: NotificationStats | undefined = data.stats
          ? { ...data.stats, unread: Math.max(0, data.stats.unread - 1) }
          : data.stats;

        qc.setQueryData<NotificationsListResponse>(key, {
          ...data,
          data: nextRows,
          total: unreadOnly ? Math.max(0, data.total - 1) : data.total,
          unread_total: Math.max(0, data.unread_total - 1),
          stats: nextStats,
        });
      }

      return { previous };
    },
    onError: (_err, _id, ctx) => {
      const snap = ctx as { previous?: Array<[readonly unknown[], NotificationsListResponse]> } | undefined;
      snap?.previous?.forEach(([key, data]) => {
        qc.setQueryData(key, data);
      });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useMarkAllNotificationsReadMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
